/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState, useEffect, useRef } from 'react';
import { Text, View } from 'react-native';
import { PlayerManager } from '@24i/react-native-player';
import { AdvertisementSelection, SeekingRestriction, ITimelineEventPlayed, AdvertisementSeek } from '@24i/player-base';
import { PlayerData, TimeInS } from 'types';
import { t } from 'Locale';
import { useStore } from 'Store';
import { isLive } from '../../../PlayerContainer/hooks/useAdvertisements';
import defaultStyles from './styles';

const STATUS: Status = {
    STARTED: 'Started',
    STOPPED: 'Stopped'
};

const twoDigits = (num) => String(num).padStart(2, '0');

const Countdown = ({
    playerInstance,
    setAlertMessage,
    gracePeriodInSeconds,
    item,
    alertTimeout,
    mediaHouse
}: CountdownProps) => {
    const [secondsRemaining, setSecondsRemaining] = useState<TimeInS>(gracePeriodInSeconds);
    const [status, setStatus] = useState<StatusState>(STATUS.STOPPED);
    const interval = useRef<NodeJS.Timeout | null>(null);
    const { setIsGracePeriod } = useStore();

    const secondsToDisplay = secondsRemaining % 60;
    const minutesRemaining = (secondsRemaining - secondsToDisplay) / 60;
    const minutesToDisplay = minutesRemaining % 60;

    const handleStop = () => {
        clearInterval(interval.current as NodeJS.Timeout);
        setStatus(STATUS.STOPPED);
        setIsGracePeriod(false);

        playerInstance.seekingRestrictions = {
            advertisementSkippablePersists: false,
            advertisementFilter: ITimelineEventPlayed.PLAYED,
            contentSeek: SeekingRestriction.UNRESTRICTED,
            skippableSeek: SeekingRestriction.UNRESTRICTED,
            unskippableSeek: SeekingRestriction.NO_FORWARD,
            advertisementSeek: AdvertisementSeek.UNRESTRICTED,
            advertisementSnapback: false,
            advertisementSelection: isLive(item) ?
                AdvertisementSelection.ALL : AdvertisementSelection.LAST,
        };
    };

    const handleStart = () => {
        setStatus(STATUS.STARTED);

        playerInstance.seekingRestrictions = {
            advertisementSkippablePersists: false,
            advertisementFilter: ITimelineEventPlayed.PLAYED,
            contentSeek: SeekingRestriction.UNRESTRICTED,
            skippableSeek: SeekingRestriction.UNRESTRICTED,
            unskippableSeek: SeekingRestriction.UNRESTRICTED,
            advertisementSnapback: false,
            advertisementSeek: AdvertisementSeek.UNRESTRICTED,
            advertisementSelection: AdvertisementSelection.NONE
        };

        const gracePeriodEnds = new Date().getTime() + gracePeriodInSeconds * 1000;
        setIsGracePeriod(true);

        interval.current = setInterval(() => {
            const sec = Math.round((gracePeriodEnds - new Date().getTime()) / 1000);
            if (sec >= 0) {
                setSecondsRemaining(sec);
            } else {
                handleStop();
            }
        }, 1000);
    };

    const showAlertForGracePeriod = () => {
        if (alertTimeout.current) clearTimeout(alertTimeout.current);
        // setAlertMessage(t('COMMON.'));
        setAlertMessage(mediaHouse ?
            t(`AD_SKIPPING.${mediaHouse}.SEEKING_GRACE_PERIOD_MSG`) :
            t('AD_SKIPPING.SEEKING_GRACE_PERIOD_MSG')
        );
        alertTimeout.current = setTimeout(() => {
            setAlertMessage('');
            clearTimeout(alertTimeout.current);
        }, 20000);
    };

    useEffect(() => {
        handleStart();
        if (secondsRemaining) showAlertForGracePeriod();

        return () => {
            clearInterval(interval.current as NodeJS.Timeout);
            setIsGracePeriod(false);
        };
    }, []);

    if (!gracePeriodInSeconds || status === 'Stopped') return <></>;

    return (
        <>
            {secondsRemaining !== 0 && (
                <View style={defaultStyles.container}>
                    <View style={defaultStyles.textContainer}>
                        <Text style={defaultStyles.text}>
                            {mediaHouse ?
                                t(`AD_SKIPPING.${mediaHouse}.SEEKING_GRACE_PERIOD_COUNTER`) :
                                t('AD_SKIPPING.SEEKING_GRACE_PERIOD_COUNTER')
                            }
                        </Text>
                        <Text style={defaultStyles.counter}>
                            {twoDigits(minutesToDisplay)}:{twoDigits(secondsToDisplay)}
                        </Text>
                    </View>
                </View>
            )}
        </>
    );
};

export default Countdown;

interface CountdownProps {
    setAlertMessage: React.Dispatch<React.SetStateAction<string>>;
    gracePeriodInSeconds: TimeInS;
    playerInstance: PlayerManager;
    item: PlayerData['item'];
    alertTimeout: React.MutableRefObject<NodeJS.Timeout>;
    mediaHouse: string | null;
}
type Status = {
    STARTED: 'Started';
    STOPPED: 'Stopped';
};

type StatusKeys = keyof typeof STATUS;

type StatusState = typeof STATUS[StatusKeys];
